import { CaseState, RiskMatrixAxis, RiskMatrixDefinition } from 'app/area/case/types-store';
import { entityValue } from 'app/infrastructure/store/entity';
import { getById } from 'app/infrastructure/store/query';
import {
  contextToStorePath,
  getEntityContext,
  getCaseData
} from 'app/infrastructure/ui/renderer/resolvers';
import { StoreData, ComplexValue } from 'app/types-shared';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';

export const mapRiskMatrixDefinitionToStoreFormat = (wireFormat: any) => {
  return {
    ...wireFormat,
    ...{
      axes: [
        ...wireFormat.axes.map(a => ({
          ...a,
          data: {
            ...a.data,
            options: a.data.options.map(o => codeItemToStoreValue(o))
          }
        }))
      ],
      cells: [
        ...wireFormat.cells.map(c => ({
          axisValues: c.axisValues,
          data: {
            riskArea: codeItemToStoreValue(c.data.riskArea),
            weight: c.data.weight
          }
        }))
      ]
    }
  } as RiskMatrixDefinition;
};

const categoryFilter = consequenceType => o =>
  o.categoryId === undefined || o.categoryId === consequenceType;

export const createAxes = (
  def: RiskMatrixDefinition,
  consequenceType: number
) => {
  const defaultAxisConfig = {
    editable: false,
    fakeAxis: true,
    data: {
      description: '',
      options: [{ value: 1, description: '' } as ComplexValue<number>],
      codetableId: 0,
      pleaseSelectDescription: ''
    }
  };

  const createAxisConfig = (axisId: number) => {
    const axisIndex = def.axes.findIndex(a => a.id === axisId);
    const axis =
      axisIndex > -1
        ? def.axes[axisIndex]
        : { ...defaultAxisConfig, id: axisId };
    return {
      ...axis,
      data: {
        ...axis.data,
        options: axis.data.options.filter(categoryFilter(consequenceType))
      }
    };
  };

  const axes = def.axes.map(a => createAxisConfig(a.id));

  // Create default axis if one is missing
  const requiredAxisIds = [0, 1];

  requiredAxisIds
    .filter(
      requiredAxisId => axes.findIndex(a => a.id === requiredAxisId) === -1
    )
    .map(requiredAxisId =>
      axes.push({ ...defaultAxisConfig, id: requiredAxisId })
    );

  return axes;
};

export const createAxisValueCombinations = (
  axes: RiskMatrixAxis[],
  consequenceType: number
) => {
  // Recursively select.
  const getCombinations = (axisIndex, axisValues, accumulatedCombinations) => {
    const axis = axes[axisIndex];
    // Add every combination for every other axis.
    for (const otherOption of axis.data.options.filter(
      categoryFilter(consequenceType)
    )) {
      const vals = [...axisValues, otherOption];

      if (axisIndex + 1 < axes.length) {
        getCombinations(axisIndex + 1, vals, accumulatedCombinations);
      } else {
        accumulatedCombinations.push([...axisValues, otherOption]);
      }
    }
  };

  const combinations = [];
  getCombinations(0, [], combinations);

  return combinations;
};

export const transformToComponentFormat = (
  storeDefinition: { definition: RiskMatrixDefinition; fetching: boolean },
  uiEngineProps: any
): RiskMatrixDefinition => {
  const consequenceType = uiEngineProps.resolveContext.primaryKey.value;

  if (storeDefinition.fetching) {
    return undefined;
  }

  const baseDef = storeDefinition.definition;

  const conseqOptionToValue = o =>
    (o.extra && o.extra.severity) ||
    (o.extra && o.extra.recurrence) ||
    (o.extra && o.extra.likelihood) ||
    o.value;

  const axes = createAxes(baseDef, consequenceType);
  const combinations = createAxisValueCombinations(axes, consequenceType);

  return {
    ...baseDef,
    axes,
    cells:
      baseDef.cells.length === 0
        ? []
        : combinations.map(comb => ({
            axisValues: [...comb],
            data: baseDef.cells.find(c =>
              baseDef.axes.reduce(
                (acc, v) =>
                  acc && c.axisValues[v.id] === conseqOptionToValue(comb[v.id]),
                true
              )
            ).data
          }))
  };
};

export const preselectedRiskMatrixValues = (
  state: CaseState,
  uiEngineProps: any,
  definition: RiskMatrixDefinition
) => {
  if (!definition) {
    return [];
  }

  const caseData: StoreData = getCaseData(state, uiEngineProps);
  const context = getEntityContext(state, uiEngineProps);

  const getAxisComponent = (axisId: number) => {
    const axis = definition.axes.find(a => a.id === axisId);
    return uiEngineProps.componentDescriptor.children.find(
      c => c.codetable === axis.data.codetableId
    );
  };

  return definition.axes.reduce((acc, axis) => {
    if (axis.fakeAxis) {
      // For fake axes we default to selecting the first option.
      const value =
        axis.data &&
        axis.data.options &&
        axis.data.options.length > 0 &&
        axis.data.options[0].value;
      acc.push(value || 0);
    } else {
      const axisComponent = getAxisComponent(axis.id);
      const entity =
        axisComponent &&
        getById(caseData, contextToStorePath(axisComponent, context));

      const value = (entity &&
        entityValue(entity, axisComponent.dataTag)) as number;
      acc.push(value || 0);
    }

    return acc;
  }, []);
};
